import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { LogOut, Scan, Users, FileText, TrendingUp, AlertCircle, Shield, BarChart3, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import NotificationBell from '@/components/NotificationBell';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalStudents: 0,
    activePayments: 0,
    expiringPayments: 0,
    todayAttendance: 0
  });
  const [recentAttendance, setRecentAttendance] = useState([]);
  
  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const fetchDashboardData = async () => {
    try {
      // Fetch students
      const studentsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/students`, {
        credentials: 'include'
      });
      const students = await studentsRes.json();
      
      // Check payment statuses
      let activeCount = 0;
      let expiringCount = 0;
      
      for (const student of students) {
        const paymentRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/student/${student.student_id}`,
          { credentials: 'include' }
        );
        const payments = await paymentRes.json();
        
        if (payments.length > 0) {
          const latestPayment = payments[0];
          const expiresAt = new Date(latestPayment.expires_at);
          const now = new Date();
          const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
          
          if (expiresAt >= now) {
            if (daysUntilExpiry <= 3) {
              expiringCount++;
            } else {
              activeCount++;
            }
          }
        }
      }
      
      // Fetch today's attendance
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const attendanceRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/attendance?start_date=${today.toISOString()}`,
        { credentials: 'include' }
      );
      const attendanceData = await attendanceRes.json();
      
      setStats({
        totalStudents: students.length,
        activePayments: activeCount,
        expiringPayments: expiringCount,
        todayAttendance: attendanceData.records.length
      });
      
      // Get recent 5 attendance records
      setRecentAttendance(attendanceData.records.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error al cargar datos');
    }
  };
  
  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  return (
    <div className="min-h-screen bg-black page-transition">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_kickbox-guard/artifacts/0qoeaod0_copy_EFC5F12B-4CDF-459A-BE8A-4BDC3F8667B9_Original.jpeg"
              alt="BKA Logo"
              className="h-10 w-auto"
            />
            <h1 className="text-2xl font-heading text-white uppercase" data-testid="admin-title">PANEL ADMIN</h1>
          </div>
          <div className="flex items-center space-x-2">
            <NotificationBell />
            <Button onClick={handleLogout} variant="ghost" size="sm" data-testid="logout-button">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto p-4 space-y-6 pb-20">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button
            onClick={() => navigate('/admin/scanner')}
            data-testid="scanner-button"
            className="h-24 bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase tracking-wider shadow-[0_0_15px_rgba(229,9,20,0.3)] transition-all active:scale-95 flex flex-col items-center justify-center space-y-2"
          >
            <Scan className="h-8 w-8" />
            <span>ESCANEAR QR</span>
          </Button>
          
          <Button
            onClick={() => navigate('/admin/students')}
            data-testid="students-button"
            className="h-24 bg-secondary hover:bg-secondary/80 text-white rounded-sm font-medium uppercase tracking-wide flex flex-col items-center justify-center space-y-2"
          >
            <Users className="h-8 w-8" />
            <span>ALUMNOS</span>
          </Button>
          
          <Button
            onClick={() => navigate('/admin/reports')}
            data-testid="reports-button"
            className="h-24 bg-secondary hover:bg-secondary/80 text-white rounded-sm font-medium uppercase tracking-wide flex flex-col items-center justify-center space-y-2"
          >
            <FileText className="h-8 w-8" />
            <span>REPORTES</span>
          </Button>
        </div>
        
        {/* Secondary Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button
            onClick={() => navigate('/admin/users')}
            data-testid="users-button"
            className="h-16 bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm font-medium uppercase tracking-wide flex items-center justify-center space-x-3 border border-zinc-800"
          >
            <Shield className="h-6 w-6 text-primary" />
            <span>USUARIOS</span>
          </Button>
          
          <Button
            onClick={() => navigate('/admin/analytics')}
            data-testid="analytics-button"
            className="h-16 bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm font-medium uppercase tracking-wide flex items-center justify-center space-x-3 border border-zinc-800"
          >
            <BarChart3 className="h-6 w-6 text-green-500" />
            <span>ANALYTICS</span>
          </Button>
          
          <Button
            onClick={() => navigate('/admin/branches')}
            data-testid="branches-button"
            className="h-16 bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm font-medium uppercase tracking-wide flex items-center justify-center space-x-3 border border-zinc-800"
          >
            <Building2 className="h-6 w-6 text-blue-500" />
            <span>SUCURSALES</span>
          </Button>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-950 border-zinc-800 p-4" data-testid="stat-total-students">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-primary" />
                <span className="text-xs uppercase tracking-wider text-zinc-400">Total Alumnos</span>
              </div>
              <div className="text-3xl font-bold font-heading text-white">{stats.totalStudents}</div>
            </div>
          </Card>
          
          <Card className="bg-green-500/10 border-green-500/20 p-4" data-testid="stat-active-payments">
            <div className="space-y-2">
              <span className="text-xs uppercase tracking-wider text-zinc-400">Pagos Activos</span>
              <div className="text-3xl font-bold font-heading text-green-500">{stats.activePayments}</div>
            </div>
          </Card>
          
          <Card className="bg-yellow-500/10 border-yellow-500/20 p-4" data-testid="stat-expiring-payments">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                <span className="text-xs uppercase tracking-wider text-zinc-400">Por Vencer</span>
              </div>
              <div className="text-3xl font-bold font-heading text-yellow-500">{stats.expiringPayments}</div>
            </div>
          </Card>
          
          <Card className="bg-zinc-950 border-zinc-800 p-4" data-testid="stat-today-attendance">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                <span className="text-xs uppercase tracking-wider text-zinc-400">Hoy</span>
              </div>
              <div className="text-3xl font-bold font-heading text-white">{stats.todayAttendance}</div>
            </div>
          </Card>
        </div>
        
        {/* Recent Attendance */}
        <Card className="bg-zinc-950 border-zinc-800 p-6">
          <h3 className="text-lg font-heading text-white uppercase mb-4">ASISTENCIA RECIENTE</h3>
          {recentAttendance.length === 0 ? (
            <p className="text-zinc-500 text-center py-8">No hay registros hoy</p>
          ) : (
            <div className="space-y-3">
              {recentAttendance.map((record) => {
                const timestamp = new Date(record.timestamp);
                const recordKey = record.attendance_id || `${record.student_id}-${record.timestamp}`;
                let paymentColor = 'text-red-500';
                if (record.payment_status === 'valid') paymentColor = 'text-green-500';
                else if (record.payment_status === 'expiring') paymentColor = 'text-yellow-500';
                return (
                  <div 
                    key={recordKey}
                    className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm border border-zinc-800"
                    data-testid={`attendance-record-${recordKey}`}
                  >
                    <div>
                      <p className="text-white font-medium">{record.student_name}</p>
                      <p className="text-sm text-zinc-400">{record.discipline}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-mono text-sm">
                        {timestamp.toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <span className={`text-xs uppercase tracking-wider ${paymentColor}`}>
                        {record.payment_status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
